import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export const LANGS = [
	{ code: 'pt', label: 'pt-br', nameKey: 'menu.language.options.pt' },
	{ code: 'en', label: 'en', nameKey: 'menu.language.options.en' },
	{ code: 'es', label: 'es', nameKey: 'menu.language.options.es' },
	{ code: 'fr', label: 'fr', nameKey: 'menu.language.options.fr' }
] as const;

export type LangCode = (typeof LANGS)[number]['code'];

@Injectable({
	providedIn: 'root'
})
export class LanguageService {
	private readonly translate = inject(TranslateService);
	private readonly storageKey = 'model-language';
	private readonly legacyStorageKey = 'lang';

	readonly langs = LANGS;
	readonly currentLang = signal<LangCode>('pt');

	readonly currentMeta = computed(
		() => this.langs.find((lang) => lang.code === this.currentLang()) ?? this.langs[0]
	);

	readonly otherLangs = computed(() =>
		this.langs.filter((lang) => lang.code !== this.currentLang())
	);

	initialize(): void {
		this.translate.addLangs(this.langs.map((lang) => lang.code));
		this.translate.setDefaultLang('pt');
		this.translate.setFallbackLang('pt');

		this.setLang(this.resolveInitialLang());
	}

	setLang(lang: LangCode): void {
		if (!this.isSupportedLang(lang)) return;

		this.currentLang.set(lang);
		this.translate.use(lang);

		localStorage.setItem(this.storageKey, lang);
		localStorage.removeItem(this.legacyStorageKey);

		document.documentElement.lang = lang === 'pt' ? 'pt-BR' : lang;
	}

	applyUserPreference(lang: LangCode | null | undefined): void {
		if (!lang) {
			this.initialize();
			return;
		}

		this.setLang(lang);
	}

	private resolveInitialLang(): LangCode {
		const stored = localStorage.getItem(this.storageKey);
		if (this.isSupportedLang(stored)) return stored;

		const legacyStored = localStorage.getItem(this.legacyStorageKey);
		if (this.isSupportedLang(legacyStored)) return legacyStored;

		const browserLang = this.translate.getBrowserLang();
		if (this.isSupportedLang(browserLang)) return browserLang;

		return 'pt';
	}

	private isSupportedLang(lang: unknown): lang is LangCode {
		return this.langs.some((item) => item.code === lang);
	}
}
